import { boxedFieldStates } from '../shared/boxedField';
import { insetHighlight } from '../shared/insetHighlight';
import styled, { css, keyframes } from 'styled-components';
import { Button } from '../button';

export const dotPulse = keyframes`
	0%, 100% { opacity: 0.3; transform: scale(0.8); }
	50% { opacity: 1; transform: scale(1); }
`;

// A band of light sweeping left to right across the text, the way other assistants show work.
export const shimmer = keyframes`
	from { background-position: 150% 0; }
	to { background-position: -50% 0; }
`;

export const fadeIn = keyframes`
	from { opacity: 0; transform: translateY(4px); }
	to { opacity: 1; transform: translateY(0); }
`;

export const messageEntrance = css`
	animation: ${fadeIn} 0.2s ease-out;
`;

// ─── AIChat ───────────────────────────────────────────────────────────────────

export const AIChatRoot = styled.div<{ $height?: string | number; $maxWidth?: string | number }>`
	display: flex;
	flex-direction: column;
	background: ${props => props.theme.aiChat.card.background};
	border-radius: 4px;
	box-shadow: ${props => props.theme.aiChat.card.shadow};
	overflow: hidden;
	/* No stroke: the inset highlight edges it, like the nav and page header. */
	${insetHighlight}
	min-width: 320px;
	max-width: ${props => props.$maxWidth !== undefined
		? (typeof props.$maxWidth === 'number' ? `${props.$maxWidth}px` : props.$maxWidth)
		: '100%'};
	height: ${props => props.$height !== undefined
		? (typeof props.$height === 'number' ? `${props.$height}px` : props.$height)
		: '600px'};
	font-family: ${props => props.theme.font};
`;

// ─── AIChatHeader ─────────────────────────────────────────────────────────────

export const HeaderContainer = styled.div`
	border-bottom: 1px solid ${props => props.theme.aiChat.header.border};
	background: ${props => props.theme.aiChat.header.background};
	flex-shrink: 0;
`;

export const HeaderTitleRow = styled.div`
	display: flex;
	align-items: baseline;
	justify-content: space-between;
	padding: 10px 16px;
`;

export const HeaderTitle = styled.h2`
	font-family: ${props => props.theme.font};
	font-size: 16px;
	font-weight: 600;
	color: ${props => props.theme.aiChat.header.title};
	margin: 0;
`;

// ─── AIChatInput ──────────────────────────────────────────────────────────────

export const InputContainer = styled.div`
	display: flex;
	align-items: flex-end;
	gap: 8px;
	padding: 12px 16px;
	background: ${props => props.theme.aiChat.card.background};
	flex-shrink: 0;
`;

// The same boxed field as Input: transparent, the form stroke, and its hover/focus states.
export const InputTextarea = styled.textarea`
	flex: 1;
	resize: none;
	${boxedFieldStates}
	padding: 8px 12px;
	font-family: ${props => props.theme.font};
	font-size: 14px;
	line-height: 1.5;
	color: ${props => props.theme.form.input.color};
	background: ${props => props.theme.form.input.background};
	overflow-y: hidden;
	box-sizing: border-box;
	display: block;
	min-height: 38px;

	&::placeholder {
		color: ${props => props.theme.form.input.placeholderColor};
	}

	&:focus {
		outline: none;
	}

	&:disabled {
		opacity: ${props => props.theme.form.disabled.opacity};
		cursor: not-allowed;
	}
`;

export const SendButton = styled(Button)`
	min-width: 37px;
	min-height: 37px;
`;

// ─── AIChatMessage ────────────────────────────────────────────────────────────

export const MessageWrapper = styled.div<{ $isUser: boolean; $continued: boolean }>`
	display: flex;
	align-items: flex-end;
	gap: 8px;
	${props => props.$isUser && css`flex-direction: row-reverse;`}
	margin-top: ${props => props.$continued ? '4px' : '16px'};
	${messageEntrance}

	strong {
		font-weight: 700 !important;
	}
`;

export const MessageAvatarSlot = styled.div<{ $visible: boolean }>`
	width: 40px;
	height: 40px;
	border-radius: 50%;
	background-size: cover;
	background-position: center;
	flex-shrink: 0;
	${props => !props.$visible && css`visibility: hidden;`}
`;

export const MessageBubbleContent = styled.div`
	display: flex;
	flex-direction: column;
	gap: 4px;
	max-width: 80%;
`;

export const MessageBubble = styled.div<{ $isUser: boolean; $isError: boolean }>`
	padding: 10px 14px;
	border-radius: ${props => props.$isUser ? '8px 8px 2px 8px' : '8px 8px 8px 2px'};
	font-size: 14px;
	line-height: 1.5;
	word-break: break-word;

	${props => {
		if (props.$isError) return css`
			background: ${props.theme.aiChat.message.error.background};
			color: ${props.theme.aiChat.message.error.color};
			border: 1px solid ${props.theme.aiChat.message.error.border};
		`;
		if (props.$isUser) return css`
			background: ${props.theme.aiChat.message.user.background};
			color: ${props.theme.aiChat.message.user.color};
		`;
		return css`
			background: ${props.theme.aiChat.message.assistant.background};
			color: ${props.theme.aiChat.message.assistant.color};
		`;
	}}

	p { margin: 0 0 8px; }
	p:last-child { margin-bottom: 0; }
	/* Links sit in prose, so they carry the underline at rest to be findable mid-sentence. */
	a {
		color: inherit;
		text-decoration: underline;
		text-underline-offset: 2px;

		&:hover {
			color: ${props => props.theme.text.primary};
			text-decoration-thickness: 2px;
		}
	}
	pre {
		background: ${props => props.theme.fill.dimmed};
		padding: 8px;
		border-radius: 4px;
		overflow-x: auto;
		margin: 4px 0;
	}
	code { font-size: 12px; }
`;

export const MessageExtraContentWrapper = styled.div`
	margin-top: 4px;
`;

// ─── AIChatMessageList ────────────────────────────────────────────────────────

export const MessageListContainer = styled.div`
	flex: 1;
	overflow-y: auto;
	padding: 16px;
	background: ${props => props.theme.aiChat.messageList.background};
	display: flex;
	flex-direction: column;
`;

export const MessageListEmptyStateWrapper = styled.div`
	flex: 1;
	display: flex;
	align-items: center;
	justify-content: center;
`;

// ─── AIChatModeToggle ─────────────────────────────────────────────────────────

export const ModeToggleWrapper = styled.div`
	padding: 8px 16px;
	border-bottom: 1px solid ${props => props.theme.aiChat.header.border};
`;

// ─── AIChatTypingIndicator ────────────────────────────────────────────────────

export const TypingIndicatorWrapper = styled.div`
	display: flex;
	align-items: flex-end;
	gap: 8px;
	margin-top: 16px;
`;

export const TypingIndicatorAvatarSlot = styled.div<{ $visible: boolean }>`
	width: 40px;
	height: 40px;
	border-radius: 50%;
	background-size: cover;
	background-position: center;
	flex-shrink: 0;
	${props => props.$visible
		? css`background-color: ${props.theme.aiChat.message.assistant.background};`
		: css`display: none;`}
`;

export const TypingIndicatorBubble = styled.div`
	padding: 12px 16px;
	border-radius: 18px 18px 18px 4px;
	background: ${props => props.theme.aiChat.message.assistant.background};
	display: flex;
	align-items: center;
	gap: 5px;
`;

export const TypingIndicatorDot = styled.span<{ $delay: number }>`
	width: 8px;
	height: 8px;
	border-radius: 50%;
	background: ${props => props.theme.aiChat.message.assistant.color};
	display: inline-block;
	animation: ${dotPulse} 1.2s ease-in-out infinite;
	animation-delay: ${props => props.$delay}ms;
	`;

// ─── AIChatWaveIndicator ──────────────────────────────────────────────────────

// Muted text with a brighter band gliding over it. The band is the signal, not the colour.
export const WaveIndicatorText = styled.div`
	padding: 10px;
	font-size: 13px;
	font-family: ${props => props.theme.font};
	color: ${props => props.theme.text.tertiary};
	background: linear-gradient(
		90deg,
		${props => props.theme.text.tertiary} 0%,
		${props => props.theme.text.tertiary} 40%,
		${props => props.theme.text.primary} 50%,
		${props => props.theme.text.tertiary} 60%,
		${props => props.theme.text.tertiary} 100%
	);
	background-size: 200% 100%;
	-webkit-background-clip: text;
	background-clip: text;
	-webkit-text-fill-color: transparent;
	animation: ${shimmer} 2s linear infinite;

	@media (prefers-reduced-motion: reduce) {
		animation: none;
		-webkit-text-fill-color: currentColor;
	}
`;

// ─── AIChatSteps ──────────────────────────────────────────────────────────────

export const StepList = styled.ul`
	list-style: none;
	margin: 16px 0 0;
	padding: 0;
	display: flex;
	flex-direction: column;
	gap: 6px;
	font-size: 13px;
	line-height: 1.4;
`;

export const StepItem = styled.li<{ $error: boolean }>`
	display: flex;
	align-items: center;
	gap: 8px;
	color: ${props => props.$error ? props.theme.text.danger : props.theme.text.tertiary};

	.icon {
		font-size: 12px;
	}
`;
